import React, { ReactNode } from 'react';
import cn from 'clsx';

import Typography from '../../library/typography/typography';
import { MapMarkerAltSolidIcon } from '../../shared/icons/icons';
import CheckInventoryButton from '../check-inventory-button/check-inventory-button';
import ProductAvailabilityMessage from '../product-availability-message/product-availability-message.one-dt';

import {
  useMyStore,
  useProductEntryDataLayerValues
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { getPrioritizedArticleStatusProduct } from '../../shared/utils/product/product';
import { getStoreDataLayerValues } from '../../shared/utils/store/store';

import { BundleData, ProductData } from '../../shared/types/graphql-types';

import styles from './product-availability.scss';

interface ProductAvailabilityProps {
  className?: string;
  hideCheckAvailabilityButton?: boolean;
  iconAvailable?: ReactNode;
  onCheckInventoryClick?: () => void;
  prebuiltPackage?: BundleData;
  product?: ProductData;
  productSet?: Array<ProductData>;
  quantity?: number;
  rearProduct?: ProductData | null;
  rearQuantity?: number;
  renderAddToCartBtn?: () => void;
  shouldShowNearbyStoreStock?: boolean;
  tabIndex?: number;
}

function ProductAvailability(props: ProductAvailabilityProps) {
  const {
    className,
    hideCheckAvailabilityButton,
    iconAvailable,
    onCheckInventoryClick,
    prebuiltPackage,
    product,
    productSet,
    quantity,
    rearProduct,
    rearQuantity,
    renderAddToCartBtn,
    shouldShowNearbyStoreStock,
    tabIndex = 0
  } = props;

  const { myStore } = useMyStore();
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();

  const articleStatusProduct = prebuiltPackage
    ? getPrioritizedArticleStatusProduct(
        prebuiltPackage?.product1,
        prebuiltPackage?.product2
      )
    : getPrioritizedArticleStatusProduct(product, rearProduct);

  const productStock = shouldShowNearbyStoreStock
    ? articleStatusProduct?.nearByStoreStock
    : articleStatusProduct?.stock;

  const {
    availabilityMessage,
    storeCode: stockStoreCode,
    storeStreetAddress: stockStoreAddressLine1
  } = productStock || {};

  const shouldShowAltStoreStock =
    (Boolean(stockStoreCode) && myStore?.code !== stockStoreCode) ||
    shouldShowNearbyStoreStock;

  const checkAvailabilityButtonText = shouldShowAltStoreStock ? (
    <>
      <MapMarkerAltSolidIcon className={styles['icon']} />{' '}
      <span className={styles['alt-store-address']}>
        {stockStoreAddressLine1?.toLowerCase()}
      </span>
    </>
  ) : (
    <>
      <MapMarkerAltSolidIcon className={styles['icon']} /> My Store
    </>
  );

  if (!availabilityMessage) {
    return null;
  }

  return (
    <div
      className={cn(
        styles['container'],
        `${className || ''} product-availability`
      )}
      data-testid="product-availability"
    >
      <ProductAvailabilityMessage
        availabilityMessage={availabilityMessage}
        iconAvailable={iconAvailable}
      />
      {hideCheckAvailabilityButton ? (
        <>{checkAvailabilityButtonText}</>
      ) : (
        <CheckInventoryButton
          buttonText={checkAvailabilityButtonText}
          className={cn(
            styles['check-inv-button'],
            'product-availability__check-inv-btn'
          )}
          hideCartItems
          initialQuantity={quantity}
          initialRearQuantity={rearQuantity}
          onCheckInventoryClick={onCheckInventoryClick}
          onSetStoreClick={results => {
            const storeDataLayerValues = getStoreDataLayerValues(
              results?.storeData
            );
            let entry;

            if (rearProduct) {
              entry = { product, rearProduct };
            } else if (prebuiltPackage) {
              entry = prebuiltPackage;
            } else {
              entry = product;
            }

            trackAction('product_change_store', {
              ...getProductEntryDataLayerValues(entry, quantity, rearQuantity),
              ...storeDataLayerValues
            });
          }}
          productItem={{
            prebuiltPackage,
            product,
            productSet,
            rearProduct
          }}
          renderAddToCartBtn={renderAddToCartBtn}
          tabIndex={tabIndex}
        />
      )}
      {shouldShowAltStoreStock && (
        <Typography tag="span">
          ,&nbsp;
          {
            myStore?.nearByStores?.find(s => s?.code === stockStoreCode)
              ?.distance
          }{' '}
          mi away
        </Typography>
      )}
    </div>
  );
}

export default ProductAvailability;
